"use client";

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import EditablePDFForm from "../../components/EditablePDFForm";
import axios from "axios";
import { showSuccess, showInfo, showWarning } from "../../utils/toastUtils";
import { offeringsAPI } from "../../services/api";
import { getBrowserId } from "../../utils/browserUtils";


const HomeIcon = () => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ cursor: "pointer" }}
    onClick={() => window.location.href = "/home"}
  >
    <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
  </svg>
);

const RegularPrayerFormLayout = () => {
  const { type = "regular" } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState("");
  const [forms, setForms] = useState([]);
  const [config, setConfig] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartMessage, setCartMessage] = useState({ text: '', type: '' });


  const styles = {
    mainContent: {
      marginLeft: isMobile ? "0" : "100px",
      paddingTop: "80px",
      flex: 1,
        padding: isMobile ? "100px 10px 20px" : "100px 20px 20px",
      width: isMobile ? "100%" : "calc(100% - 400px)",
      backgroundColor: "#f5f5f5",
    },
    breadcrumb: {
      display: "flex",
      alignItems: "center",
      marginBottom: "20px",
      color: "#6b7280",
      fontSize: "0.9rem",
      gap: "8px",
    },
    error: {
      color: "#ef4444",
      fontSize: "0.9rem",
      textAlign: "center",
      marginBottom: "20px",
    },
    success: {
      color: "#10b981",
      fontSize: "0.9rem",
      textAlign: "center",
      marginBottom: "20px",
      padding: "10px",
      backgroundColor: "#d1fae5",
      borderRadius: "4px",
    },
    messageContainer: {
      padding: "10px",
      borderRadius: "4px",
      marginBottom: "20px",
      textAlign: "center",
    },
  };

  const breadcrumb = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: "#6b7280", fontSize: "0.9rem" }}>
      <HomeIcon />
      <span>/</span>
      <span style={{ cursor: "pointer" }} onClick={() => window.location.href = "/home"}>Home</span>
      <span>/</span>
      <span>Online Prayer Requests</span>
      <span>/</span>
      <span>Regular Homa Request</span>
    </div>
  );

  const formRef = useRef();

  // Fetch forms and create dynamic config
  useEffect(() => {
    const fetchForms = async () => {
      try {
        setIsLoading(true);
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        
        const response = await axios.get(`${API_URL}/forms/public`);
        
        if (response.data.success) {
          // Filter forms that have permalink (not null)
          const formsWithPermalink = response.data.data.filter(form => form.permalink);
          setForms(formsWithPermalink);
          
          // Create dynamic config object
          const dynamicConfig = {};
          formsWithPermalink.forEach(form => {
            dynamicConfig[form.permalink] = {
              pdfFile: form.pdfUrl || `/api/downloads/pdf/${encodeURIComponent(form.pdfFilename)}`,
              offeringType: form.formType,
              width: form.width || 800,
              amountField: form.amountField || "amount",
              dateField: form.dateField || "date"
            };
          });
          
          setConfig(dynamicConfig);
        }
      } catch (err) {
        console.error('Error fetching forms:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError(`Failed to load form configuration: ${err.response?.data?.message || err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchForms();
  }, []);

  // Get current form config or fallback
  const currentConfig = config[type] || {
    pdfFile: "/homa.pdf",
    offeringType: "Regular Homa Request",
    width: 800,
    amountField: "Total Oering",
    dateField: "date"
  };
  
  const { pdfFile, offeringType, width, amountField, dateField } = currentConfig;

  return (
    <Layout activeMenuItem="Online Prayer Requests" breadcrumb={breadcrumb}>
      <main style={styles.mainContent}>
        {error && <p style={styles.error}>{error}</p>}
        {cartMessage.text && (
          <div 
            style={{
              ...styles.messageContainer,
              backgroundColor: cartMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
              color: cartMessage.type === 'success' ? '#10b981' : '#ef4444'
            }}
          >
            {cartMessage.text}
          </div>
        )}
        
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading form configuration...
          </div>
        ) : (
          /* Embed the EditablePDFForm component */
          <EditablePDFForm ref={formRef} pdfFile={pdfFile} width={width} style={{ overflow: 'visible' }} dateField={dateField} />
        )}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
          <button
            style={{
              background: isAddingToCart ? '#e5e7eb' : '#fff',
              color: '#111',
              border: '1px solid #111',
              borderRadius: 8,
              padding: '18px 48px',
              fontSize: '1.1rem',
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              cursor: isAddingToCart ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: isAddingToCart ? 0.7 : 1,
            }}
            disabled={isAddingToCart}
            onClick={async () => {
              if (!formRef.current) return;
              
              // Clear any previous messages
              setCartMessage({ text: '', type: '' });
              
              const data = formRef.current.getDataJson();
              const amount = data[amountField] || '';
              
              if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
                setCartMessage({
                  text: 'Please enter a valid Total Offering amount before adding to cart.',
                  type: 'error'
                });
                return;
              }
              
              try {
                setIsAddingToCart(true);
                const pdfBlob = await formRef.current.getPdfFile();
                const reader = new FileReader();
                
                reader.onloadend = async () => {
                  try {
                    const base64pdf = reader.result.split(',')[1];
                    const browserId = getBrowserId();

                    console.log('Uploading filled form to S3...');

                    // First, upload the PDF to S3
                    const token = localStorage.getItem("temple_token") || localStorage.getItem("jwtToken");
                    const uploadResponse = await fetch("/api/uploads/upload-filled-form", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        base64Data: base64pdf,
                        filename: `${offeringType.replace(/\s+/g, '-')}_${Date.now()}.pdf`,
                        mimeType: 'application/pdf'
                      })
                    });

                    const uploadResult = await uploadResponse.json();

                    if (!uploadResult.success) {
                      throw new Error(uploadResult.message || 'Failed to upload form to S3');
                    }

                    console.log('✅ Form uploaded to S3:', uploadResult.data.s3_url);
                    console.log('Adding to basket with S3 URL...');

                    // Find the current form to get orientation and report_id
                    const currentForm = forms.find(f => f.permalink === type);
                    const formOrientation = currentForm?.orientation || 'portrait';
                    const reportId = currentForm?.report_id || null;

                    console.log('Form metadata:', {
                      formType: offeringType,
                      orientation: formOrientation,
                      report_id: reportId
                    });

                    // Now add to basket with S3 URL instead of base64
                    const payload = {
                      offering_type: offeringType,
                      amount,
                      form_img: uploadResult.data.s3_url, // S3 URL instead of base64
                      form_data: {
                        ...data,
                        s3_key: uploadResult.data.s3_key,
                        s3_url: uploadResult.data.s3_url,
                        form_orientation: formOrientation,
                        report_id: reportId
                      },
                      browser_id: browserId
                    };

                    // Use the API service which automatically includes browser_id in headers
                    const response = await offeringsAPI.addToBasket(payload);
                    
                    if (response.success) {
                      window.dispatchEvent(new Event('cartUpdated'));
                      showSuccess('Item successfully added to your basket!');
                      // Redirect to basket page
                      setTimeout(() => {
                        navigate('/basket');
                      }, 500);
                    } else {
                      setCartMessage({
                        text: response.message || 'Failed to add to basket',
                        type: 'error'
                      });
                    }
                  } catch (err) {
                    console.error('Error processing cart addition:', err);
                    setCartMessage({
                      text: err.message || 'An error occurred while adding to cart',
                      type: 'error'
                    });
                  } finally {
                    setIsAddingToCart(false);
                  }
                };
                
                reader.onerror = () => {
                  setCartMessage({
                    text: 'Error processing the form',
                    type: 'error'
                  });
                  setIsAddingToCart(false);
                };
                
                reader.readAsDataURL(pdfBlob);
              } catch (err) {
                console.error('Error generating PDF:', err);
                setCartMessage({
                  text: err.message || 'Error generating the form PDF',
                  type: 'error'
                });
                setIsAddingToCart(false);
              }
            }}
          >
            {isAddingToCart ? (
              <>
                <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                ADDING...
              </>
            ) : (
              'ADD TO CART'
            )}
          </button>
        </div>
      </main>
    </Layout>
  );
};

export default RegularPrayerFormLayout;
